import config from 'config';
import classNames from 'classnames';
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { RouteComponentProps } from 'react-router-dom';

import { Avatar } from '@vyaguta/vyaguta-ui';

import * as userService from 'services/user';

import { pulseLogo } from 'assets/images';
import { isObjectEmpty } from 'utils/object';
import { handleError } from 'utils/errorHandler';
import { EMPLOYEE_PROFILE } from 'constants/routes';
import { MINIMUM_PAGE_COUNT } from 'constants/page';
import { parse, stringify } from 'utils/queryParam';
import { buildUrl, extractFullName, getEmpImageUrl, interpolate } from 'utils/string';

import useTitle from 'hooks/useTitle';
import Table from 'components/common/table';
import EmployeesFilter from './components/EmployeesFilter';
import Pagination from 'components/common/paginator';
import { USER_SELECTED_FIELDS } from 'constants/fetchTypes';
import TableHeader from 'components/common/table/TableHeader';
import {
  EMPLOYEE_SORT_BY,
  EMPLOYEE_SORT_ORDER,
  USER_FETCH_FIELDS,
  DEFAULT_PAGE_SIZE,
  DEFAULT_PAGE_NUMBER,
} from 'constants/appConstants';
import type { QueryObject } from 'interface/queryParam';

interface RouteParams {
  id?: string;
}

interface DesignationArea {
  name?: string;
}

interface DesignationValue {
  id?: number;
  name?: string;
  area?: DesignationArea;
}

interface EmployeeRow {
  id: number;
  empId?: string;
  firstName: string;
  middleName?: string | null;
  lastName: string;
  mobilePhone?: string;
  designation?: DesignationValue;
  sn: number;
}

interface PageMeta {
  total?: number;
  page?: number;
  size?: number;
}

interface CellProps<TRow, TValue> {
  value: TValue;
  original: TRow;
}

interface EmployeeRouteProps extends RouteComponentProps<RouteParams> {
  history: {
    push: (value: { pathname: string; search: string } | string) => void;
  };
  location: {
    pathname: string;
    search: string;
  };
  updateFilterState: (value: boolean) => void;
}

type SortColumn = (typeof EMPLOYEE_SORT_BY)[keyof typeof EMPLOYEE_SORT_BY];

const getQueryParams = (search: string): QueryObject =>
  parse(search, {
    decoder: function (str) {
      return decodeURIComponent(str);
    },
  }) as QueryObject;

const getNumericQueryValue = (value: QueryObject[string], fallbackValue: number): number => {
  if (typeof value === 'number') {
    return value;
  }

  if (typeof value === 'string') {
    const convertedValue = Number(value);
    return Number.isNaN(convertedValue) ? fallbackValue : convertedValue;
  }

  return fallbackValue;
};

const getSortColumn = (value: QueryObject[string]): SortColumn => {
  return value === EMPLOYEE_SORT_BY.DESIGNATION ? EMPLOYEE_SORT_BY.DESIGNATION : EMPLOYEE_SORT_BY.FIRST_NAME;
};

const isAscendingSortOrder = (value: QueryObject[string]): boolean => value !== EMPLOYEE_SORT_ORDER.DESCENDING;

const getColumns = (
  onSortChange: (column: SortColumn) => void,
  isAscendingOrdered: boolean,
  columnToSortBy: SortColumn
) => [
  {
    Header: 'SN.',
    sortable: false,
    filterable: false,
    accessor: 'sn',
    maxWidth: 80,
    Cell: (props: CellProps<EmployeeRow, number>) => {
      return (
        <span className="capital-text employees__table-text">{props.value < 10 ? `0${props.value}` : props.value}</span>
      );
    },
  },
  {
    Header: (
      <TableHeader
        label="Name"
        identifier={EMPLOYEE_SORT_BY.FIRST_NAME}
        columnToSortBy={columnToSortBy}
        isAscendingOrdered={isAscendingOrdered}
        onClick={() => onSortChange(EMPLOYEE_SORT_BY.FIRST_NAME)}
      />
    ),
    accessor: 'firstName',
    sortable: false,
    filterable: false,
    sortMethod: () => 1,
    minWidth: 180,
    Cell: (props: CellProps<EmployeeRow, string>) => {
      const fullName = extractFullName(props.original, true);
      return (
        <div className="d-flex flex-row align-items-center" data-cy="employee-image-name">
          <Avatar
            className="nametag nametag--lg profile-photo leapfroggers-profile"
            image={getEmpImageUrl(props.original.empId)}
            alt={fullName}
          />
          <span className="text-bold capital-text employees__table-text">{fullName}</span>

          <a
            onClick={(event) => {
              event.stopPropagation();
            }}
            href={interpolate(buildUrl(config.pulseBaseUrl, config.endpoints.pulse.userFeed), {
              id: props.original?.id,
            })}
            className="employees__pulse-icon"
            target="_blank"
            rel="noopener noreferrer"
          >
            <img src={pulseLogo} alt="pulse" />
          </a>
        </div>
      );
    },
  },
  {
    Header: 'Cell Number',
    sortable: false,
    filterable: false,
    accessor: 'mobilePhone',
    Cell: (props: CellProps<EmployeeRow, string>) => (
      <a
        href={`tel:${props.value}`}
        onClick={(event) => {
          event.stopPropagation();
        }}
        data-cy="employee-phone"
      >
        {props.value}
      </a>
    ),
  },
  {
    Header: (
      <TableHeader
        label="Designation"
        identifier={EMPLOYEE_SORT_BY.DESIGNATION}
        columnToSortBy={columnToSortBy}
        isAscendingOrdered={isAscendingOrdered}
        onClick={() => onSortChange(EMPLOYEE_SORT_BY.DESIGNATION)}
      />
    ),
    accessor: 'designation',
    sortable: false,
    filterable: false,
    minWidth: 150,
    Cell: (props: CellProps<EmployeeRow, DesignationValue>) => {
      const areaSuffix = props.value?.area ? `, ${props.value.area.name}` : '';
      const designationText = props.value?.id ? `${props.value.name}${areaSuffix}` : '-';

      return (
        <span className="capital-text employees__table-text" data-cy="employee-area-designation">
          {designationText}
        </span>
      );
    },
  },
];

const Employees: React.FC<EmployeeRouteProps> = (props) => {
  const { history, location, updateFilterState } = props;

  const queryParams = useMemo(() => getQueryParams(location.search), [location.search]);
  const columnToSortBy = useMemo(() => getSortColumn(queryParams.sortBy), [queryParams.sortBy]);
  const isAscendingOrdered = useMemo(() => isAscendingSortOrder(queryParams.order), [queryParams.order]);
  const [isFilterApplied, setIsFilterApplied] = useState(!isObjectEmpty(queryParams));
  const [pageData, setPageData] = useState<PageMeta>({});
  const [pageCount, setPageCount] = useState<number>(DEFAULT_PAGE_NUMBER);
  const [isDataLoading, setIsDataLoading] = useState<boolean>(true);
  const [employeeList, setEmployeeList] = useState<EmployeeRow[]>([]);

  const setTitle = useTitle();

  const updateUrl = useCallback(
    (value: QueryObject) => {
      const queryParam = stringify('', value, { arrayFormat: 'comma', encode: false });
      history.push({
        pathname: location.pathname,
        search: queryParam,
      });
    },
    [history, location.pathname]
  );

  useEffect(() => {
    setTitle('Leapfroggers');
  }, [setTitle]);

  useEffect(() => {
    (async () => {
      try {
        setIsDataLoading(true);
        const params = {
          ...queryParams,
          sortBy: columnToSortBy,
          fields: USER_SELECTED_FIELDS.join(','),
          order: isAscendingOrdered ? EMPLOYEE_SORT_ORDER.ASCENDING : EMPLOYEE_SORT_ORDER.DESCENDING,
          workingShiftId:
            typeof queryParams.workingShiftId === 'string'
              ? queryParams.workingShiftId.toUpperCase().replace(/ /g, '_')
              : queryParams.workingShiftId,
        };

        if (queryParams.roleIds) {
          params['fields'] = [USER_FETCH_FIELDS.ROLES, ...USER_SELECTED_FIELDS].join(',');
        }

        const response = await userService.fetchAll(params);
        const pageIndex = getNumericQueryValue(queryParams.page, DEFAULT_PAGE_NUMBER);
        const size = getNumericQueryValue(queryParams.size, DEFAULT_PAGE_SIZE);
        const { data } = response as { data: EmployeeRow[]; meta: PageMeta };
        const { total = 0 } = response.meta as PageMeta;
        const pgCount = Math.ceil(total / size);
        setPageData(response.meta);

        const dataWithSN = data.map((row, index) => {
          const sn = (pageIndex - 1) * size + index + 1;
          return { ...row, sn };
        });

        setEmployeeList(dataWithSN);
        setPageCount(pgCount);
      } catch (err) {
        handleError(err);
      } finally {
        setIsDataLoading(false);
      }
    })();
  }, [columnToSortBy, isAscendingOrdered, location.search, queryParams]);

  const applyFilter = (filter: QueryObject) => {
    updateUrl(filter);
  };

  const onSortChange = useCallback(
    (selectedColumn: SortColumn) => {
      const nextIsAscendingOrdered = selectedColumn !== columnToSortBy ? true : !isAscendingOrdered;

      updateUrl({
        ...queryParams,
        page: DEFAULT_PAGE_NUMBER,
        sortBy: selectedColumn,
        order: nextIsAscendingOrdered ? EMPLOYEE_SORT_ORDER.ASCENDING : EMPLOYEE_SORT_ORDER.DESCENDING,
      });
    },
    [columnToSortBy, isAscendingOrdered, queryParams, updateUrl]
  );

  const onPageChange = useCallback(
    (selectedPage: number) => {
      updateUrl({
        ...getQueryParams(location.search),
        page: selectedPage,
      });
    },

    [location.search, updateUrl]
  );

  const onPageSizeChange = useCallback(
    (selectedSize: number) => {
      setIsFilterApplied(true);
      updateUrl({
        ...getQueryParams(location.search),
        page: DEFAULT_PAGE_NUMBER,
        size: selectedSize,
      });
    },
    [location.search, updateUrl]
  );

  return (
    <main>
      <div className="container leapfroggers-table">
        <div className="full-scope-card">
          <EmployeesFilter
            {...props}
            data={pageData}
            applyFilter={applyFilter}
            updateFilterState={updateFilterState}
            isFilterApplied={isFilterApplied}
            setIsFilterApplied={setIsFilterApplied}
            isDataLoading={isDataLoading}
          />
          <div className={classNames('full-scope-card__content')}>
            <Table
              loading={isDataLoading}
              data={employeeList}
              columns={getColumns(onSortChange, isAscendingOrdered, columnToSortBy)}
              showPagination={false}
              getTrProps={(_, rowInfo: { original: EmployeeRow }) => {
                return {
                  className: 'align-items-center d-flex',
                  'data-cy': 'employee-row',
                  onClick: (e: React.MouseEvent<HTMLDivElement>) => {
                    const url = interpolate(EMPLOYEE_PROFILE, { id: rowInfo.original.id });
                    if (e.ctrlKey || e.metaKey) {
                      window.open(url);
                    } else {
                      history.push(url);
                    }
                  },
                };
              }}
            />
            <Pagination
              pageData={pageData}
              pageCount={+(pageCount || MINIMUM_PAGE_COUNT)}
              onPageChange={onPageChange}
              onPageSizeChange={onPageSizeChange}
            />
          </div>
        </div>
      </div>
    </main>
  );
};

export default Employees;
